/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {InputAutocompleteComponent} from '../../shared/input/input-autocomplete/input-autocomplete.component';
import {InputTextComponent} from '../../shared/input/input-text/input-text.component';
import {
    AbstractControl,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {objectsValidator} from '../../shared/input/input-validators';
import {WarningsEnum} from '../../shared/warnings/warnings.enum';
import {
    Observable,
    of,
    Subject,
    Subscription,
    timer
} from 'rxjs';
import {MaterialDetailModel} from '../../api/ms-claims/contracts/material-detail.model';
import {MaterialApi} from '../../api/ms-partnumberchanges/material.api';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {MandatoryMessageTypeEnum} from '../../shared/input/service/mandatory-message-type.enum';
import {InputMessagesService} from '../../shared/input/service/input-messages.service';
import {TypeaheadHelperService} from '../../shared/services/typeahead-helper.service';
import {PartNumberChangesOldCredentialsState} from '../models/part-number-changes-old-credentials.state';
import {InputDisplayModeEnum} from '../../shared/input/input-display-mode.enum';
import {PartNumberChangesDetailTrialConfigurationModel} from '../services/part-number-changes-detail-configuration/models/pnc-trial/part-number-changes-detail-trial-configuration.model';
import {PartNumberChangesDetailTrialDisplayModeEnum} from '../services/part-number-changes-detail-configuration/models/pnc-trial/part-number-changes-detail-trial-display-mode.enum';
import {InputTextareaComponent} from '../../shared/input/input-textarea/input-textarea.component';
import trackByIndex from '../../shared/utils/trackByIndex';
import {PncLotNumberInputState} from './pnc-lot-number-input/pnc-lot-number-input.state';
import {PartNumberChangesApi} from '../../api/ms-partnumberchanges/part-number-changes.api';
import {PncTypeEnum} from '../models/pnc-type-enum/pnc-type.enum';
import {PartNumberChangesStatusEnum} from '../models/part-number-changes-status.enum';
import {RouteSearchCriteriaModel} from '../../api/ms-partnumberchanges/contracts/route-search-criteria.model';
import {ReferenceDataApi} from '../../api/ms-partnumberchanges/reference-data.api';
import {InputDropdownComponent} from '../../shared/input/input-dropdown/input-dropdown.component';
import {PartNumberChangesOldCredentialsFormState} from '../models/part-number-changes-old-credentials-form.state';
import {PartNumberChangesUpdateAiMaterialModel} from '../part-number-changes-ai-solution/models/part-number-changes-update-ai-material.model';
import { v4 as uuid } from 'uuid';
import {PartNumberChangesAdditionalFullLotNumberModel} from '../models/part-number-changes-additional-full-lot-number.model';
import {PartNumberChangesAdditionalSpoNumberModel} from '../models/part-number-changes-additional-spo-number.model';
import {map} from 'rxjs/operators';
import {MaterialDetailMapper} from '../models/material-detail/material-detail.mapper';

@Component({
    selector: 'app-part-number-changes-old-credentials',
    templateUrl: './part-number-changes-old-credentials.component.html',
    styleUrls: [
        '../styles/pnc-base.component.css',
        './part-number-changes-old-credentials.component.css'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PartNumberChangesOldCredentialsComponent extends StatefulComponent<PartNumberChangesOldCredentialsState>
        implements OnInit, AfterViewInit, OnDestroy {
    /** Input variables */
    @Input() oldPartNumberCredentials: PartNumberChangesDetailTrialConfigurationModel;
    @Input() updateRouteSearchCriteriaChange: Subject<RouteSearchCriteriaModel> =
            new Subject<RouteSearchCriteriaModel>();
    @Input() triggerFormReset: Subject<void> = new Subject<void>();
    @Input() routeSearchCriteria: RouteSearchCriteriaModel;
    @Input() isHidden = false;

    /** Outputs variables */
    @Output() partNumberChangesOldPartNumberCredentials = new Subject<PartNumberChangesOldCredentialsFormState>();
    @Output() oldMaterialChange = new Subject<PartNumberChangesUpdateAiMaterialModel>();
    @Output() validForCreate = new Subject<boolean>();
    @Output() titleTrial = new Subject<string>();

    @Input() isLoading: (loading: boolean) => void;

    /** Inputs components */
    @ViewChild('oldPartNumberMaterialInput') oldPartNumberMaterialInput: InputAutocompleteComponent;
    @ViewChild('salesVariantInput') salesVariantInput: InputTextComponent;
    @ViewChild('modelCodeInputText') modelCodeInputText: InputTextComponent;
    @ViewChild('modelCodeInputDropdown') modelCodeInputDropdown: InputDropdownComponent;
    @ViewChild('colourInput') colourInput: InputTextComponent;
    @ViewChild('fullLotNumberInput') fullLotNumberInput: InputTextComponent;
    @ViewChild('quantityInput') quantityInput: InputTextComponent;
    @ViewChild('problemDescriptionInput') problemDescriptionInput: InputTextareaComponent;

    /** Inputs validators */
    validators: { [key: string]: ValidatorFn[] } = {};

    /** Inputs messages variables */
    public oldPartNumberMaterialInputMessages = new Map<WarningsEnum, string[]>();
    public salesVariantInputMessages = new Map<WarningsEnum, string[]>();
    public modelCodeInputMessages = new Map<WarningsEnum, string[]>();
    public colourInputMessages = new Map<WarningsEnum, string[]>();
    public lotNumberInputMessages = new Map<WarningsEnum, string[]>();
    public quantityInputMessages = new Map<WarningsEnum, string[]>();
    public descriptionInputMessages = new Map<WarningsEnum, string[]>();
    public WarningsEnum = WarningsEnum;
    trackByIndex = trackByIndex;

    /** Local data variables */
    private modelCodeOptions: string[];
    private supplyingPackingPlantCode: string;
    private pncFullLotNumberInputValues: PncLotNumberInputState;
    private subscriptions = new Subscription();

    private readonly WILL_BE_FILLED_BY_PARTNER_PLACEHOLDER = 'Will be filled by the partner';

    constructor(private materialApi: MaterialApi,
                private partNumberChangesApi: PartNumberChangesApi,
                private inputMessagesService: InputMessagesService,
                private referenceDataApi: ReferenceDataApi,
                public typeahead: TypeaheadHelperService,
                private changeDetector: ChangeDetectorRef) {
        super();
        this.storeState({showModelCodeDropdown: false});
    }

    get detailTrialClass(): string {
        let final = 'pnc-detail-trial';

        if (this.isDetailMode) {
            final = final + ' view-mode';
        }

        if (this.state?.oldPartNumberDisplayConfiguration?.styleClass != null) {
            final = final + ` ${this.state?.oldPartNumberDisplayConfiguration.styleClass}`;
        }

        return final;
    }

    get isSolutionAccepted(): boolean {
        return this.state?.oldPartNumberDisplayConfiguration?.isSolutionAccepted;
    }

    get isDetailMode(): boolean {
        return this.state?.oldPartNumberDisplayConfiguration?.displayMode === PartNumberChangesDetailTrialDisplayModeEnum.DETAIL;
    }

    get isSolvedStatus(): boolean {
        return this.oldPartNumberCredentials?.data?.status === PartNumberChangesStatusEnum.SOLVED;
    }

    get hasFormDot(): boolean {
        return !this.isDetailMode && this.oldPartNumberCredentials?.displayConfiguration.pncType !== PncTypeEnum.PLANNER;
    }

    get detailTrialFormGlobalClass(): string {
        let cssClass = '';
        if (this.isDetailMode) {
            cssClass = 'pnc-detail-view-form-request';
        } else {
            cssClass = 'pnc-detail-create-form-request';
        }
        return cssClass;
    }

    get isPartnerMissingPart(): boolean {
        return this.state?.oldPartNumberDisplayConfiguration?.pncType === PncTypeEnum.F2_MISSING_PARTS
                || this.state?.oldPartNumberData?.pncType === PncTypeEnum.F2_MISSING_PARTS;
    }

    get problemDescriptionHeight(): number {
        return this.state?.oldPartNumberDisplayConfiguration?.descriptionInput?.displayMode === InputDisplayModeEnum.VIEW ? 88 : 118;
    }

    get pncTypeCssClass(): string {
        return this.oldPartNumberCredentials?.displayConfiguration.pncType ?? PncTypeEnum.UNKNOWN;
    }

    get showSentBy(): boolean {
        return this.state?.oldPartNumberData?.sentBy != null;
    }

    get spoNumber(): PartNumberChangesAdditionalSpoNumberModel {
        return this.state?.formData?.spoNumber;
    }

    get additionalSpoNumbers(): PartNumberChangesAdditionalSpoNumberModel[] {
        return this.state?.formData?.additionalSpoNumbers;
    }

    get additionalSpoNumberTooltipMessage(): string {
        return `You can't add more than ` +
                this.state.oldPartNumberDisplayConfiguration?.partNumberChangeConfig?.maxNumberOfAdditionalSpoNumbers +
                ' additional SPO numbers.';
    }

    get pncType(): string {
        let pncType;

        if (!this.state?.oldPartNumberData?.pncType) {
            pncType = this.oldPartNumberCredentials?.displayConfiguration?.pncType;
        } else {
            pncType = this.state?.oldPartNumberData?.pncType;
        }

        return pncType;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngOnInit(): void {
        this.setInputValidators();
        this.setInitialState();
        this.setRouteSearchCriteriaSubscription();
        this.setResetFormSubscription();
    }

    ngAfterViewInit(): void {
        this.oldPartNumberMaterialInput?.disable();
        this.setModelCodeInputInitialStatus();

        this.changeDetector.detectChanges();
    }

    searchMaterials = (term: string): Observable<MaterialDetailModel[]> => {
        let allMaterials = of([]);
        if (term != null && term.length > 0) {
            if (this.pncType === PncTypeEnum.AI_CHANGE) {
                allMaterials = this.materialApi.getAllAiMaterials(term, this.supplyingPackingPlantCode, 0, 20)
                        .pipe(map(MaterialDetailMapper.fromMaterialDetailContractArray));
            } else {
                allMaterials = this.materialApi.getAllMaterials(term, this.supplyingPackingPlantCode, 0, 20)
                        .pipe(map(MaterialDetailMapper.fromMaterialDetailContractArray));
            }
        }
        return allMaterials;
    };

    plannerValuePipe(
            displayMode: InputDisplayModeEnum,
            cellValue: any,
            otherPipe?: (displayMode: InputDisplayModeEnum, cellValue: any) => string
    ): string {
        let value: string;

        if (this.oldPartNumberCredentials?.displayConfiguration.pncType === PncTypeEnum.PLANNER
                && displayMode === InputDisplayModeEnum.VIEW && !this.isDetailMode
                || this.state?.oldPartNumberData?.status === PartNumberChangesStatusEnum.PENDING
                && displayMode === InputDisplayModeEnum.VIEW) {
            value = this.WILL_BE_FILLED_BY_PARTNER_PLACEHOLDER;
        } else {
            value = otherPipe?.(displayMode, cellValue);
        }

        return value;
    }

    initialValuePipe(displayMode: InputDisplayModeEnum, cellValue: any): string {
        let value = '-';

        if (cellValue != null || displayMode === InputDisplayModeEnum.EDIT) {
            value = cellValue;
        }

        return value;
    }

    /**
     * Sets the new old part number/ material value
     */
    setOldPartNumberMaterialValue(material: MaterialDetailModel): void {
        this.storeState({formData: {...this.state.formData, material: material}});
        this.partNumberChangesOldPartNumberCredentials.next(this.state.formData);
        this.validForCreate.next(this.formIsValid());

        // Notify pnc ai solution component only when has a valid material
        if (this.oldPartNumberMaterialInput.isValid) {
            this.oldMaterialChange.next({
                material: material,
                materialUnavailable: false,
                oldMaterialFieldDisabled: this.oldPartNumberMaterialInput.isDisabled
            });
        }
    }

    /**
     * Sets the new sales variant value
     */
    setSalesVariantValue(salesVariant: string): void {
        const salesVariantValue =
                this.isValidCodeOption(salesVariant, true) ? salesVariant.toUpperCase() : null;
        if (salesVariant !== this.WILL_BE_FILLED_BY_PARTNER_PLACEHOLDER) {
            this.storeState({formData: {...this.state.formData, salesVariant: salesVariantValue}});
            this.partNumberChangesOldPartNumberCredentials.next(this.state.formData);
            this.validForCreate.next(this.formIsValid());
        }
    }

    /**
     * Sets the new model code value
     */
    setModelCodeValue(modelCode: string): void {
        const modelCodeValue =
                this.isValidCodeOption(modelCode, true) ? modelCode.toUpperCase() : null;
        if (modelCode !== this.WILL_BE_FILLED_BY_PARTNER_PLACEHOLDER) {
            this.storeState({formData: {...this.state.formData, modelCode: modelCodeValue}});
            this.partNumberChangesOldPartNumberCredentials.next(this.state.formData);
            this.validForCreate.next(this.formIsValid());
        }
    }

    /**
     * Sets the new colour value
     */
    setColourValue(colour: string): void {
        const colourCodeValue =
                this.isValidCodeOption(colour, true) ? colour.toUpperCase() : null;
        if (colour !== this.WILL_BE_FILLED_BY_PARTNER_PLACEHOLDER) {
            this.storeState({formData: {...this.state.formData, colourCode: colourCodeValue}});
            this.partNumberChangesOldPartNumberCredentials.next(this.state.formData);
            this.validForCreate.next(this.formIsValid());
        }
    }

    /**
     * Sets the new full lot number value
     */
    setFullLotNumberValue(lotNumber: string): void {
        const lotNumberValue =
                this.isValidCodeOption(lotNumber, true) ? lotNumber : null;
        if (lotNumber !== this.WILL_BE_FILLED_BY_PARTNER_PLACEHOLDER) {
            this.storeState({formData: {...this.state.formData, fullLotNumber: lotNumberValue ? lotNumberValue.toUpperCase() : null}});
            this.partNumberChangesOldPartNumberCredentials.next(this.state.formData);
            this.validForCreate.next(this.formIsValid());
        }
    }

    /**
     * Sets the new quantity value
     */
    setQuantityValue(quantity: number): void {
        const quantityValue =
                this.isValidCodeOption(quantity, true) ? quantity : null;
        this.storeState({formData: {...this.state.formData, quantity: quantityValue}});
        this.partNumberChangesOldPartNumberCredentials.next(this.state.formData);
        this.validForCreate.next(this.formIsValid());
    }

    /**
     * Sets the new problem description value
     */
    setProblemDescriptionValue(problemDescription: string): void {
        const problemDescriptionValue = problemDescription?.length > 0 ? problemDescription : null;
        this.storeState({formData: {...this.state.formData, problemDescription: problemDescriptionValue}});
        this.partNumberChangesOldPartNumberCredentials.next(this.state.formData);
        this.validForCreate.next(this.formIsValid());
    }

    /**
     * Manages the old part number/material input status changes, adding errors and warnings as needed
     */
    manageOldPartNumberMaterialInputStateChange(material: AbstractControl): void {
        if (this.state?.oldPartNumberDisplayConfiguration?.materialInput?.required) {
            this.inputMessagesService.mandatoryWarnings(material,
                    this.oldPartNumberMaterialInputMessages,
                    MandatoryMessageTypeEnum.CREATE);
        }

        this.inputMessagesService.invalidInputValueError(material,
                this.oldPartNumberMaterialInputMessages,
                'material');
        this.oldPartNumberMaterialInputMessages = new Map(this.oldPartNumberMaterialInputMessages);
    }

    /**
     * Manages the sales variant input status changes, adding errors and warnings as needed
     */
    manageSalesVariantInputStateChange(salesVariant: AbstractControl): void {
        this.inputMessagesService.invalidInputValueError(salesVariant,
                this.salesVariantInputMessages,
                'sales variant. e.g. A7');
        this.salesVariantInputMessages = new Map(this.salesVariantInputMessages);
    }

    /**
     * Manages the model code input status changes, adding errors and warnings as needed
     */
    manageModelCodeInputStateChange(modelCode: AbstractControl): void {
        const invalidMessage = this.state?.showModelCodeDropdown ? 'model code' : 'model code. e.g. G20';

        if (this.state?.oldPartNumberDisplayConfiguration?.modelCodeInput?.required) {
            this.inputMessagesService.mandatoryWarnings(modelCode,
                    this.modelCodeInputMessages,
                    MandatoryMessageTypeEnum.CREATE);
        }

        this.inputMessagesService.invalidInputValueError(modelCode,
                this.modelCodeInputMessages,
                invalidMessage);
        if (this.state?.hasGroups != null && !this.state?.hasGroups) {
            this.modelCodeInputMessages.delete(WarningsEnum.ERROR);
            this.modelCodeInputMessages.set(WarningsEnum.ERROR, ['The current PNC route is no longer active']);
        }
        this.modelCodeInputMessages = new Map(this.modelCodeInputMessages);
    }

    /**
     * Manages the colour input status changes, adding errors and warnings as needed
     */
    manageColourInputStateChange(colour: AbstractControl): void {
        this.inputMessagesService.invalidInputValueError(colour,
                this.colourInputMessages,
                'colour code. e.g. WC4L');
        this.colourInputMessages = new Map(this.colourInputMessages);
    }

    /**
     * Manages the full lot number input status changes, adding errors and warnings as needed
     */
    manageFullLotNumberInputStateChange(lotNumber: AbstractControl): void {
        if (this.state?.oldPartNumberDisplayConfiguration?.fullLotNumberInput?.required) {
            this.inputMessagesService.mandatoryWarnings(lotNumber,
                    this.lotNumberInputMessages,
                    MandatoryMessageTypeEnum.CREATE);
        }

        this.inputMessagesService.invalidInputValueError(lotNumber,
                this.lotNumberInputMessages,
                'Full LOT number. e.g. E4R5T7896');
        this.lotNumberInputMessages = new Map(this.lotNumberInputMessages);
    }

    /**
     * Manages the quantity input status changes, adding errors and warnings as needed
     */
    manageQuantityInputStateChange(quantity: AbstractControl): void {
        if (this.state?.oldPartNumberDisplayConfiguration?.quantityInput?.required) {
            this.inputMessagesService.mandatoryWarnings(quantity,
                    this.quantityInputMessages,
                    MandatoryMessageTypeEnum.CREATE);
        }

        this.inputMessagesService.invalidInputValueError(quantity,
                this.quantityInputMessages,
                'quantity e.g. 1');
        this.quantityInputMessages = new Map(this.quantityInputMessages);
    }

    /**
     * Manages the description input status changes, adding errors and warnings as needed
     */
    manageDescriptionInputStateChange(description: AbstractControl): void {
        if (this.state?.oldPartNumberDisplayConfiguration?.descriptionInput?.required) {
            this.inputMessagesService.mandatoryWarnings(description,
                    this.descriptionInputMessages,
                    MandatoryMessageTypeEnum.CREATE);
        }
        this.descriptionInputMessages = new Map(this.descriptionInputMessages);
    }

    showViewMode(displayMode: InputDisplayModeEnum): boolean {
        return displayMode === InputDisplayModeEnum.VIEW;
    }

    warningLevel(required: boolean): WarningsEnum {
        return required ? WarningsEnum.WARNING_CREATE : null;
    }

    updateLotNumbers(event: PncLotNumberInputState): void {
        this.pncFullLotNumberInputValues = event;

        this.storeState({
            formData: {
                ...this.state.formData,
                fullLotNumber: event.fullLotNumber.fullLotNumber,
                quantity: event.fullLotNumber.quantity,
                additionalFullLotNumbers: event.additionalFullLotNumbers
            }
        });

        this.partNumberChangesOldPartNumberCredentials.next(this.state.formData);
        this.validForCreate.next(this.formIsValid());
    }

    updateSpoNumbers(event: PartNumberChangesAdditionalSpoNumberModel): void {
        this.storeState({
            ...this.state, formData: {
                ...this.state.formData,
                spoNumber: event
            }
        });
        this.partNumberChangesOldPartNumberCredentials.next(this.state.formData);
        this.validForCreate.next(this.formIsValid());
    }

    updateAdditionalSpoNumber(event: PartNumberChangesAdditionalSpoNumberModel): void {
        const spoNumbers = [...this.state.formData.additionalSpoNumbers];
        const spoNumberIndex = spoNumbers?.findIndex((spoNumber) => spoNumber.id === event?.id);

        if (event) {
            if (spoNumberIndex >= 0) {
                spoNumbers[spoNumberIndex] = event;
            } else {
                spoNumbers.push(event);
            }
        }

        this.storeState({
            ...this.state, formData: {
                ...this.state.formData,
                additionalSpoNumbers: spoNumbers
            }
        });

        this.partNumberChangesOldPartNumberCredentials.next(this.state.formData);
        this.validForCreate.next(this.formIsValid());
    }

    /**
     * Model codes input filter function
     */
    searchModelCodes = (term: string): Observable<string[]> => {
        if (term != null && term.length > 0) {
            return of(this.modelCodeOptions.filter((item) => (`${item}`)
                    .toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));
        }
        return of(this.modelCodeOptions);
    };

    addAdditionalSpoNumber(): void {
        this.storeState({
            formData: {
                ...this.state.formData, additionalSpoNumbers: [
                    ...this.state.formData?.additionalSpoNumbers ?? [],
                    {
                        spoNumber: null,
                        isValid: null,
                        id: uuid()
                    }
                ]
            }
        });
    }

    deleteAdditionalSpoNumber(id: string): void {
        this.storeState({
            formData: {
                ...this.state.formData,
                additionalSpoNumbers: this.state.formData?.additionalSpoNumbers?.filter((spoNumber) => spoNumber.id !== id)
            }
        });
        this.partNumberChangesOldPartNumberCredentials.next(this.state.formData);
        this.validForCreate.next(this.formIsValid());
    }

    canAddAdditionalSpoNumber(): boolean {
        return this.state?.oldPartNumberDisplayConfiguration?.partNumberChangeConfig?.maxNumberOfAdditionalSpoNumbers >
                (this.state?.formData?.additionalSpoNumbers ?? []).length;
    }

    trackByItem = (index: number, item: PartNumberChangesAdditionalFullLotNumberModel): string => item.id;

    showSpoNumber(): boolean {
        return (this.oldPartNumberCredentials?.displayConfiguration.pncType === PncTypeEnum.PLANNER ||
                this.oldPartNumberCredentials?.displayConfiguration.pncType === PncTypeEnum.AI_CHANGE ||
                this.state?.oldPartNumberData?.pncType === PncTypeEnum.PLANNER ||
                this.state?.oldPartNumberData?.pncType === PncTypeEnum.AI_CHANGE);
    }

    private setInputValidators(): void {
        this.validators = {
            oldPartNumberMaterial: [objectsValidator],
            salesVariant: [Validators.pattern(/^[A-Za-z]\d$/)],
            modelCode: [Validators.pattern(/^[A-Za-z]\d{2}|^[A-Za-z]{2}\d$/)],
            modelCodeDropdown: [this.modelCodeDropdownValidator],
            colour: [Validators.pattern(/^[A-Za-z\d]{4}$/)],
            fullLotNumber: [Validators.pattern(/^[E|e]\w{4}\d{4}$/)],
            quantity: [Validators.pattern(/^(?!0)\d{1,6}$/)]
        };
    }

    private setInitialState(): void {
        this.storeState({oldPartNumberData: this.oldPartNumberCredentials?.data});
        this.storeState({oldPartNumberDisplayConfiguration: this.oldPartNumberCredentials?.displayConfiguration});
        this.storeState({trialRejectionMotivation: this.state?.oldPartNumberData?.trialRejectionDetail});

        if (this.state?.oldPartNumberData != null) {
            this.storeState({
                formData: {
                    material: this.state?.oldPartNumberData.material,
                    salesVariant: this.state?.oldPartNumberData.salesVariant,
                    modelCode: this.state?.oldPartNumberData.modelCode,
                    colourCode: this.state?.oldPartNumberData.colourCode,
                    fullLotNumber: this.state?.oldPartNumberData.fullLotNumber,
                    quantity: this.state?.oldPartNumberData.quantity,
                    problemDescription: this.state?.oldPartNumberData.problemDescription,
                    type: this.state?.oldPartNumberData.pncType,
                    spoNumber: this.setSpoNumber(),
                    additionalSpoNumbers: this.setAdditionalSpoNumbers()
                }
            });
            this.partNumberChangesOldPartNumberCredentials.next(this.state?.formData);
        }
        this.titleTrial.next(this.state.oldPartNumberData?.title);
        this.validForCreate.next(this.formIsValid());
    }

    private setSpoNumber(): PartNumberChangesAdditionalSpoNumberModel {
        return {
            spoNumber: this.state?.oldPartNumberData?.spoNumber,
            id: uuid(),
            isValid: true
        };
    }

    private setAdditionalSpoNumbers(): PartNumberChangesAdditionalSpoNumberModel[] {

        return this.state?.oldPartNumberData?.additionalSpoNumbers?.map((spoNumber) => ({
                    spoNumber: spoNumber,
                    id: uuid(),
                    isValid: true
                })
        );
    }

    private setRouteSearchCriteriaSubscription(): void {
        this.subscriptions.add(
                this.updateRouteSearchCriteriaChange
                        .subscribe((routeCriteria: RouteSearchCriteriaModel) => {
                            this.supplyingPackingPlantCode = routeCriteria?.supplyingPackingPlantCode;
                            this.manageMaterialInputAfterPlantChange(this.supplyingPackingPlantCode);
                            this.manageModelCodeInputAfterChange(routeCriteria);
                        })
        );
    }

    private setResetFormSubscription(): void {
        this.subscriptions.add(this.triggerFormReset.subscribe(() => this.resetForm()));
    }

    private setModelCodeInputInitialStatus(): void {
        if (this.state?.oldPartNumberDisplayConfiguration?.displayMode === PartNumberChangesDetailTrialDisplayModeEnum.DETAIL
                && this.state?.oldPartNumberDisplayConfiguration?.modelCodeInput?.displayMode === InputDisplayModeEnum.EDIT) {
            this.manageModelCodeInputAfterChange(this.routeSearchCriteria);
        } else {
            this.disableModelCodeInput();
        }
    }

    private setModelCodeValueAfterInputChange = (): void => {
        timer(0)
                .subscribe(() => {
                    if (this.state?.hasGroups) {
                        const modelCode = this.state?.formData?.modelCode;
                        if (this.state?.showModelCodeDropdown && this.modelCodeInputDropdown != null) {
                            if (this.modelCodeOptions.length === 1) {
                                this.modelCodeInputDropdown?.disable();
                                this.modelCodeInputDropdown.setValue(this.modelCodeOptions[0]);
                            } else {
                                this.modelCodeInputDropdown?.enable();
                                this.modelCodeInputDropdown.setValue(modelCode);
                            }
                        } else if (this.modelCodeInputText != null) {
                            this.modelCodeInputText?.enable();
                            this.modelCodeInputText.setValue(modelCode);
                        }
                    } else {
                        this.disableModelCodeInput();
                        this.markModelCodeInputAsInvalid();
                    }
                });
    };

    private disableModelCodeInput(): void {
        if (this.state.showModelCodeDropdown) {
            this.modelCodeInputDropdown?.disable();
        } else {
            this.modelCodeInputText?.disable();
        }
    }

    private markModelCodeInputAsInvalid(): void {
        if (this.state.showModelCodeDropdown) {
            this.modelCodeInputDropdown?.markAsInvalid();
        } else {
            this.modelCodeInputText?.markAsInvalid();
        }
        this.validForCreate.next(this.formIsValid());
    }

    private modelCodeDropdownValidator = (control: AbstractControl): object => {
        if (control.value != null && !this.modelCodeOptions?.includes(control.value)) {
            return {valid: false};
        } else {
            return null;
        }
    };

    /**
     * Manages the material input value and disabled / enabled status after supplying packing plant change
     */
    private manageMaterialInputAfterPlantChange(plant: string): void {
        const material = this.state?.formData?.material;

        if (plant != null) {
            this.oldPartNumberMaterialInput.enable();
            this.oldPartNumberMaterialInputMessages.delete(WarningsEnum.WARNING);

            if (material != null && material.number != null) {
                this.validateMaterial(material?.number, plant);
            }
        } else {
            this.oldPartNumberMaterialInput.disable();
        }
    }

    /**
     * Manages the model code input value, type and enable / disable status after ship to party change
     */
    private manageModelCodeInputAfterChange(searchCriteria: RouteSearchCriteriaModel): void {
        if (searchCriteria?.partnerCode != null
                && searchCriteria?.shipToPartyCode != null
                && searchCriteria?.supplyingPackingPlantCode != null) {

            if (searchCriteria.partnerCode !== this.state?.technicianGroupSearchCriteria?.partnerCode
                    || searchCriteria.shipToPartyCode !== this.state?.technicianGroupSearchCriteria?.shipToPartyCode
                    || searchCriteria.supplyingPackingPlantCode !== this.state?.technicianGroupSearchCriteria?.supplyingPackingPlantCode) {

                this.isLoading?.(true);

                this.subscriptions.add(
                        this.referenceDataApi.getActiveTechnicianGroups({
                            partnerCode: searchCriteria.partnerCode,
                            shipToPartyCode: searchCriteria.shipToPartyCode,
                            supplyingPackingPlantCode: searchCriteria.supplyingPackingPlantCode
                        })
                                .subscribe((groups) => {
                                    const modelCodes = groups.flatMap((entry) => entry.modelCodes);
                                    this.storeState({
                                        showModelCodeDropdown: modelCodes.length > 0,
                                        hasGroups: groups.length > 0
                                    });
                                    this.modelCodeOptions = modelCodes?.sort();
                                    this.setModelCodeValueAfterInputChange();
                                })
                                .add(() => {
                                    this.isLoading?.(false);
                                }));
            }
        } else {
            this.disableModelCodeInput();
        }
        this.storeState({technicianGroupSearchCriteria: searchCriteria});
    }

    /**
     * Validates a material from a specific supplying packing plant
     * @param materialNumber The material number to be validated
     * @param plantCode The plant code for which to check if the material is valid
     */
    private validateMaterial(materialNumber: string, plantCode: string): void {
        this.isLoading?.(true);
        this.subscriptions.add(
                this.materialApi.getAllMaterials(materialNumber, plantCode, 0, 20)
                        .subscribe((validation) => {
                            if (validation.length === 0) {
                                this.oldPartNumberMaterialInput.markAsInvalid();
                                this.oldPartNumberMaterialInputMessages.set(WarningsEnum.ERROR,
                                        ['This material is not available in the selected packing plant']);
                                this.validForCreate.next(this.formIsValid());

                                // Notify that old part number material change is invalid
                                this.oldMaterialChange.next({
                                    material: null,
                                    materialUnavailable: true,
                                    oldMaterialFieldDisabled: false
                                });
                            }
                        })
                        .add(() => {
                            this.isLoading?.(false);
                        })
        );
    }

    /**
     * Determines is a option that contains a code attribute is valid. if the code is option, valid is considered.
     * @param option the option to check
     * @param optional the flag optional
     * @returns boolean if the option is valid
     */
    private isValidCodeOption = (option: any, optional: boolean = false): boolean =>
            option != null && option !== '' && (optional || option.code != null);

    /**
     * Checks if every input is valid
     * @returns boolean if all the inserted inputs values are valid
     */
    private formIsValid(): boolean {
        return this.fromInputsAreValid() && this.formInputsValuesAreValid();
    }

    private fromInputsAreValid(): boolean {
        return this.modelCodeIsValid()
                // Inputs that are common on all PNC type
                && (this.state?.oldPartNumberDisplayConfiguration?.materialInput?.displayMode === InputDisplayModeEnum.VIEW
                        || this.oldPartNumberMaterialInput?.isValid)
                && (this.state?.oldPartNumberDisplayConfiguration?.salesVariantInput?.displayMode === InputDisplayModeEnum.VIEW
                        || this.salesVariantInput?.isValid)
                && (this.state?.oldPartNumberDisplayConfiguration?.colourInput?.displayMode === InputDisplayModeEnum.VIEW
                        || this.colourInput?.isValid)
                // Inputs that are different by PNC type
                && this.fullLotNumberIsValid()
                && (!this.showSpoNumber() || this.spoNumberIsValid());
    }

    private formInputsValuesAreValid(): boolean {
        return (!this.state?.oldPartNumberDisplayConfiguration?.materialInput?.required || this.state?.formData?.material?.number != null)
                && (!this.state?.oldPartNumberDisplayConfiguration?.salesVariantInput?.required
                        || this.state?.formData?.salesVariant != null)
                && (!this.state?.oldPartNumberDisplayConfiguration?.modelCodeInput?.required || this.state?.formData?.modelCode != null)
                && (!this.state?.oldPartNumberDisplayConfiguration?.colourInput?.required || this.state?.formData?.colourCode != null)
                && (!this.state?.oldPartNumberDisplayConfiguration?.fullLotNumberInput?.required
                        || this.state?.formData?.fullLotNumber != null)
                && (!this.state?.oldPartNumberDisplayConfiguration?.quantityInput?.required || this.state?.formData?.quantity != null)
                && (!this.state?.oldPartNumberDisplayConfiguration?.descriptionInput?.required
                        || this.state?.formData?.problemDescription != null);
    }

    private fullLotNumberIsValid(): boolean {
        let valid: boolean;
        if (this.isPartnerMissingPart) {
            valid = this.isValidFullLotNumberForF2MissingPart();
        } else {
            valid = (this.state?.oldPartNumberDisplayConfiguration?.fullLotNumberInput?.displayMode === InputDisplayModeEnum.VIEW
                    || this.fullLotNumberInput?.isValid)
                    && (this.state?.oldPartNumberDisplayConfiguration?.quantityInput?.displayMode === InputDisplayModeEnum.VIEW
                            || this.quantityInput?.isValid);
        }
        return valid;
    }

    private isValidFullLotNumberForF2MissingPart(): boolean {
        const isValid = (item: PartNumberChangesAdditionalFullLotNumberModel): boolean =>
                item.isValid === true && item.fullLotNumber != null && item.fullLotNumber !== '';

        return this.state?.oldPartNumberDisplayConfiguration?.fullLotNumberInput?.displayMode === InputDisplayModeEnum.VIEW
                || (isValid(this.pncFullLotNumberInputValues?.fullLotNumber)
                        && !this.pncFullLotNumberInputValues?.additionalFullLotNumbers?.find((item) => isValid(item) === false));
    }

    private spoNumberIsValid(): boolean {
        return (this.spoNumber?.isValid && !this.spoNumber?.spoNumber && !(this.additionalSpoNumbers?.length > 0))
                || (this.spoNumber?.isValid && this.spoNumber?.spoNumber && !this.additionalSpoNumbers?.find(
                        (item) => item.isValid === false || item.spoNumber === '' || item.spoNumber == null));
    }

    private modelCodeIsValid(): boolean {
        return this.state?.oldPartNumberDisplayConfiguration?.modelCodeInput?.displayMode === InputDisplayModeEnum.VIEW
                || (this.state?.showModelCodeDropdown ? this.modelCodeInputDropdown?.isValid : this.modelCodeInputText?.isValid);
    }

    private resetForm = (): void => {
        this.oldPartNumberMaterialInput.reset();
        this.salesVariantInput.reset();
        this.colourInput.reset();
        this.fullLotNumberInput?.reset();
        this.quantityInput?.reset();
        this.problemDescriptionInput.reset();

        if (this.state?.showModelCodeDropdown) {
            this.modelCodeInputDropdown.reset();
        } else {
            this.modelCodeInputText.reset();
        }

        this.storeState({
            formData: {
                ...this.state?.formData,
                material: null,
                salesVariant: null,
                colourCode: null,
                fullLotNumber: null,
                quantity: null,
                problemDescription: null,
                modelCode: null,
                additionalFullLotNumbers: null,
                spoNumber: null,
                additionalSpoNumbers: null
            }
        });
        this.partNumberChangesOldPartNumberCredentials.next(this.state?.formData);
    };
}
